import React, { useState, useEffect, useRef, useContext } from "react";
import { StyleSheet, ScrollView, Text, TouchableOpacity, View as RNView, Platform, Modal as RNModal, Pressable } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { MaterialCommunityIcons, Feather } from "@expo/vector-icons";
import { useIsFocused } from "@react-navigation/native";
import { replaceDataValues } from "../../utils/common";
import { View } from "../../components/shared/Themed";
import { LabelBannerWithSearchBar } from "../../components/custom/Banners/LabelBannerWithSearchBar";
import { LabelBannerWithSearchTags } from "../../components/custom/Banners/LabelBannerWithSearchTags";
import { DataTableWithPagination } from "../../components/custom/DataTable/DataTableWithPagination";
import { default as Modal } from "../../components/custom/Modal/index";
import Spinner from "../../components/shared/Spinner";
import useColorScheme from "../../hooks/useColorScheme";
import { userClaimInformation } from "../../models/usersModel";
import { validSignedURL } from "../../utils/validateSignedUrl";

import _ from "lodash";

import {
  getDelivery
} from "../../services/deliveryService";
import { headers, mdHeaders, smHeaders } from "./utils/deliveryHeaders";
import {
  nestedStockTransferHeaders as deliveryHeader,
  mdNestedStockTransferHeaders as deliveryMdHeaders,
  smNestedHeaders as deliverySmHeaders,
} from "./utils/deliveryDetailsHeaders";
import { deliveryModel } from "../../models/reportsModel";
import {
  filterBasedOnAnyKey,
} from "../../utils/searchInArrays";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import { useSelector } from "react-redux";
import { handleTableLoader } from "../../utils/common";
import { sortColumns } from "../../utils/sortColumns";

export default function StockTransferScreen({ route, navigation }: any) {
  const scrollRef = useRef<ScrollView>(null);
  const isFocused = useIsFocused();
  const colorScheme = useColorScheme();

  const columnsToNotHaveThousandsSeparator: Array<string> = ["Document #", "Reference", "Document # ", "DocNum"];
  const dataToBeConverted: object = {
    /*{ "searchString" : "replaceValue" }*/
    "Dispatched": "Shipped",
    "Not Confirmed": "Pending for Payment"
  };


  const { userInfo } = useSelector((state: any) => state.configurations);
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const [isImageLoading, setImageLoading] = useState<boolean>(true);
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [deliveryPageLevelList, setDeliveryPageLevelList] = useState<
    Array<deliveryModel>
  >([]);


  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);

  const { userRoles } = useSelector((state: any) => state.configurations);

  const [stockTransferListFilterCategoryList] =
    useState(["all", "NSW", "VIC", "QLD", "WA", "SA", "TAS", "Today", "Next 7 days", "Next 14 days", "Next 30 days", "Transfer In", "Transfer Out"]);

  // Multi-select filter arrays
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

  const dateFilterOptions = ["All", "Today", "Next 7 days", "Next 14 days", "Next 30 days"];
  const stateFilterOptions = ["all", "NSW", "VIC", "QLD", "WA", "SA", "TAS"];
  const typeFilterOptions = ["all", "Transfer In", "Transfer Out"];

  const { stockTransferList, isStockTransferListLoaded } = useSelector(
    (state: any) => state.stockTransfer
  );

  const { stockTransferItemsList } = useSelector((state: any) => state.items);

  const [selected, setSelected] = useState<deliveryModel>(
    {} as unknown as deliveryModel
  );

  //Table & Nested Table Headers
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      headers
    );

  const [nestedTableHeaders, setNestedTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      deliveryHeader
    );

  const [_isDisplayViewOn, _setIsDisplayViewOn] = useState(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const [, setContentOffSet] = useState<number>(0);

  const [columnSettingsOpen, setColumnSettingsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [settingsBtnPos, setSettingsBtnPos] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const settingsBtnRef = useRef<any>(null);

  // Load saved hidden columns from storage on mount
  useEffect(() => {
    AsyncStorage.getItem("stock_transfer_hidden_columns").then((stored: string | null) => {
      if (stored) {
        try {
          setHiddenColumns(JSON.parse(stored));
        } catch (e) { }
      }
    });
  }, []);

  const getAllHeaders = () => {
    const base = isLargeDevice ? headers : isMediumDevice ? mdHeaders : smHeaders;
    const { CustomerPortalOrderNo, ...rest } = base as any;
    return rest;
  };

  const getFilteredHeaders = (baseHeaders: Record<string, Record<string, string | number | boolean>>) => {
    const visibleEntries = Object.entries(baseHeaders).filter(([key]) => !hiddenColumns.includes(key));
    const totalOriginalFlex = Object.values(baseHeaders).reduce((sum, h: any) => sum + (Number(h.width) || 0), 0);
    const totalVisibleFlex = visibleEntries.reduce((sum, [, h]: any) => sum + (Number(h.width) || 0), 0);
    const scale = totalVisibleFlex > 0 ? (totalOriginalFlex / totalVisibleFlex) : 1;

    const result: Record<string, Record<string, string | number | boolean>> = {};
    visibleEntries.forEach(([key, value]: any) => {
      result[key] = { ...value, width: Number(value.width) * scale };
    });
    return result;
  };

  const toggleColumn = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const updated = prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey];
      AsyncStorage.setItem("stock_transfer_hidden_columns", JSON.stringify(updated));
      return updated;
    });
  };

  const filterDeliveryByCategory = (
    _deliveryMode: Array<deliveryModel>,
    _selectedDates: string[],
    _selectedStates: string[],
    _selectedTypes: string[]
  ) => {
    const today = new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate() - 1
    );
    const tmw = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    const Nxt7Days = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7);
    const Nxt14Days = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 14);
    const Nxt30Days = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 30);

    return _deliveryMode.filter((delivery: deliveryModel) => {
      const reqDate = new Date(
        new Date(delivery.RequestDate).getFullYear(),
        new Date(delivery.RequestDate).getMonth(),
        new Date(delivery.RequestDate).getDate()
      );

      const checkDateFilter = (d: string) => {
        switch (d) {
          case "Today": return reqDate >= today && reqDate <= tmw;
          case "Next 7 days": return reqDate >= today && reqDate <= Nxt7Days;
          case "Next 14 days": return reqDate >= today && reqDate <= Nxt14Days;
          case "Next 30 days": return reqDate >= today && reqDate <= Nxt30Days;
          default: return false;
        }
      };

      const dateOk = _selectedDates.length === 0 || _selectedDates.some(d => checkDateFilter(d));
      const stateOk = _selectedStates.length === 0 || _selectedStates.some(s => delivery.State === s);
      const typeOk = _selectedTypes.length === 0 || _selectedTypes.some(t => delivery.DocType === t);

      return dateOk && stateOk && typeOk;
    });
  };

  const onHandleSortColumn = (columnKey: string) => {
    const _sortedDeliveryList: Array<deliveryModel> = sortColumns(
      deliveryPageLevelList,
      columnKey,
      tableHeaders,
      setTableHeaders
    );

    setDeliveryPageLevelList(filterDeliveryByCategory(_sortedDeliveryList, selectedDates, selectedStates, selectedTypes));
  };

  /**
   * TODO: NESTED TABLE SORTED Is Disabled,
   * enable when sorting on nested table feature is requested
   */
  // const onHandleNestedSortColumn = (
  //   columnKey: string,
  //   columnParentIdentifier: string
  // ) => {
  //   const _sortedItemsList: Array<itemsModal> = sortColumns(
  //     nestedItemsPageLevelList,
  //     columnKey,
  //     nestedTableHeaders,
  //     setNestedTableHeaders
  //   );

  //   setNestedItemsPageLevelList(_sortedItemsList);
  // };

  const onHandleScroll = (event: any) => {
    setContentOffSet(Math.floor(event.nativeEvent?.contentOffset?.y) ?? 0);
  };

  const onHandleRowClick = (row: deliveryModel) => { };

  const fadeTableLoader = (setIsSearched: (e: boolean) => void) => {
    handleTableLoader(false, true, setIsSearched);
  };

  const onHandleRemoveRow = (containerId: string) => { };

  const onHandleSearch = (searchText: string) => {
    displayTableLoader(setIsSearched);

    let searchedDelivery: Array<deliveryModel> = [];
    if (searchText.trim() !== "") {
      /**
       * When SearchBy: Delivery, enter into if block
       * otherwise go for else block
       */
      if (!_isDisplayViewOn) {
        const filteredDelivery = filterBasedOnAnyKey(
          extractIdenticalDelivery(),
          searchText
        );

        searchedDelivery = feedItemsInDelivery(filteredDelivery);
      } else {
        /**
         * Steps to search for items within containers
         * 1: Extract all items from containers & union them
         * 2: Search by keyword in the list of items extracted from containers
         * 3: Group by container_number on the searchedItems
         * 4: Remove the duplicates from array of container_numbers
         * 5: filter the unique containers from the identicalFeededDeliveryWithItems()
         *    & return the list of containers with items matching the search text
         */

        //Step 1
        const itemsFromDelivery = _.map(
          identicalFeededDeliveryWithItems(),
          "items"
        );
        const flattenItems = _.flatten(itemsFromDelivery);

        //Step 2
        const searchedItems = filterBasedOnAnyKey(flattenItems, searchText);

        //Step 3
        const groupedDelivery = Object.keys(
          _.groupBy(searchedItems, "DocNum")
        );

        //Step 4
        const uniqueDelivery = _.uniq(groupedDelivery);

        //Step 5
        const filteredDelivery = identicalFeededDeliveryWithItems().filter(
          (delivery) => uniqueDelivery.includes(delivery.DocNum)
        );

        searchedDelivery = filteredDelivery;
      }
      setDeliveryPageLevelList(filterDeliveryByCategory(searchedDelivery, selectedDates, selectedStates, selectedTypes));
    }

    setTimeout(() => {
      fadeTableLoader(setIsSearched);
    }, 2000);
  };

  const onHandleScrollToBase = () => {
    scrollRef.current?.scrollTo({
      x: 0,
      y: 0,
      animated: true,
    });
  };

  useEffect(() => {
    validSignedURL(
      selected.ItemCode || "",
      "label",
      setImageLoading,
      setSelectedImage
    );
  }, [selected]);

  const ModalContent = () => {
    const _excludedKeys = [
      "BaseReference",
      "BatchAttribute1",
      "BatchAttribute2",
      "BatchAttribute3",
      "U_CashCusName",
      "U_CashCusPhone",
      "U_PMX_BAV1",
      "U_PMX_BAV2",
      "U_PMX_BAV3",
      "LastUpdate",
      "OrderQtyPCS",
      "SQRef.",
      "PaymentTerm",
      "RemainingQtyPCS"
    ];

    const _orderedKeys = [
      "DocNum",
      "DocType",
      "ItemCode",
      "ProductGroup",
      "CustomerCode",
      "CustomerRef",
      "DeliveryAddress",
      "OrderDate",
      "RequestDate",
      "DelivrdQty",
      "OrderQty",
      "RemainingQty",
      "ShipToCode",
      "ShippingType",
      "State",
      "Warehouse",
      "SalesPerson",
      "ProcessBy"
    ];

    const _specialLabels = {
      DocType: {
        oldLabel: "Document",
        newLabel: "Document Type",
      },
      ItemCode: {
        oldLabel: "ItemCode",
        newLabel: "Item Code",
      },
      ProductGroup: {
        oldLabel: "ProductGroup",
        newLabel: "Product Group",
      },
      CustomerCode: {
        oldLabel: "CustomerCode",
        newLabel: "Customer Code",
      },
      CustomerRef: {
        oldLabel: "CustomerCode",
        newLabel: "Customer Ref#",
      },
      DeliveryAddress: {
        oldLabel: "DeliveryAddress",
        newLabel: "Delivery Address",
      },
      OrderDate: {
        oldLabel: "OrderDate",
        newLabel: "Order Date",
      },
      RequestDate: {
        oldLabel: "RequestDate",
        newLabel: "Request Date",
      },
      DelivrdQty: {
        oldLabel: "DelivrdQty",
        newLabel: "Delivery Qty",
      },
      OrderQty: {
        oldLabel: "OrderQty",
        newLabel: "Order Qty",
      },
      RemainingQty: {
        oldLabel: "RemainingQty",
        newLabel: "Remaining Qty",
      },
      SalesPerson: {
        oldLabel: "SalesPerson",
        newLabel: "Sales",
      },
      ProcessBy: {
        oldLabel: "ProcessBy",
        newLabel: "Operator",
      },
    };

    return (
      <Modal.ModalContent
        title={selected.ItemCode || ""}
        subTitle={selected.Description}
        icon="folder"
        uri={selectedImage}
        loading={isImageLoading}
        childern={
          <Modal.ModalBody
            listData={selected}
            excludedKeys={_excludedKeys}
            renameList={headers}
            orderedKeys={_orderedKeys}
            specialLabels={_specialLabels}
            columnsToNotHaveThousandSeparator={columnsToNotHaveThousandsSeparator}
          />
        }
      />
    );
  };

  const extractIdenticalDelivery = () => {
    //process both containers and items list to return filtered containers list
    const _stockTransferItemsList: Array<deliveryModel> = stockTransferList;
    const groupDelivery = _.groupBy(
      _stockTransferItemsList,
      (item) => item.DocNum
    );
    // check here
    return _stockTransferItemsList as Array<deliveryModel>;
  };

  const feedItemsInDelivery = (delivery: deliveryModel[]) => {
    const _stockTransferItemsList: Array<deliveryModel> = [...stockTransferList];

    return delivery.map((_delivery) => {
      return {
        ..._delivery,
        items: _stockTransferItemsList.filter(
          (item: deliveryModel) =>
            item.DocNum === _delivery.DocNum
        ),
      };
    });
  };

  const identicalFeededDeliveryWithItems = (): Array<deliveryModel> => {
    return feedItemsInDelivery(extractIdenticalDelivery());
  };

  const displayTableLoader = (setIsSearched: React.Dispatch<React.SetStateAction<boolean>>) => {
    handleTableLoader(true, false, setIsSearched);
  };


  useEffect(() => {
    setRefresh(true);
  }, [_isDisplayViewOn]);

  useEffect(() => {
    let isMounted: boolean = true;
    const _userInfo = userInfo as userClaimInformation;
    // Avoid keep refreshing table when item is selected
    if (isMounted) {
      if (deliveryPageLevelList.length === 0 || isRefresh) {
        displayTableLoader(setIsSearched);
        // filters reset on refresh — selectedDates/selectedStates/selectedTypes remain user choice
        getDelivery(
          "StockTransferReport",
          "",
          _userInfo.team,
          _userInfo.zone_info || "",
          "stock_transfer_report",
          tableHeaders
        );
      }
    }

    setRefresh(false);

    return () => {
      isMounted = false;
    };
  }, [isFocused, isRefresh, deliveryPageLevelList.length]);

  useEffect(() => {
    if (!isSmallDevice && !isMobileDevice) {
      setTableHeaders(getFilteredHeaders(getAllHeaders()));
    } else {
      setTableHeaders(
        isMediumDevice ? mdHeaders : smHeaders
      );
    }
    setNestedTableHeaders(
      isLargeDevice
        ? deliveryHeader
        : isMediumDevice
          ? deliveryMdHeaders
          : deliverySmHeaders
    );
  }, [hiddenColumns, isLargeDevice, isMediumDevice, isSmallDevice, isMobileDevice]);

  useEffect(() => {
    // _setFilterCategory([])
    const transferOut = [
      ...filterDeliveryByCategory(identicalFeededDeliveryWithItems(), [], [], ["Transfer Out"]).reduce((map, obj) => {
        if (!map.has(obj.DocNum)) map.set(obj.DocNum, obj);
        return map;
      }, new Map)
        .values()
    ];

    const transferIn = [
      ...filterDeliveryByCategory(identicalFeededDeliveryWithItems(), [], [], ["Transfer In"]).reduce((map, obj) => {
        if (!map.has(obj.DocNum)) map.set(obj.DocNum, obj);
        return map;
      }, new Map)
        .values()
    ];


    const unique = [...transferIn, ...transferOut];
    setDeliveryPageLevelList(filterDeliveryByCategory(unique, selectedDates, selectedStates, selectedTypes));


    setTimeout(() => {
      fadeTableLoader(setIsSearched);
    }, 2000);
  }, [stockTransferList, stockTransferItemsList, selectedDates, selectedStates, selectedTypes]);

  return (
    <ScrollView
      style={{ backgroundColor: colorScheme === "light" ? "white" : "white" }}
      onScroll={onHandleScroll}
      ref={scrollRef}
    >
      <View
        style={{ backgroundColor: colorScheme === "light" ? "white" : "white" }}
      >
        <View
          style={[
            styles.container,
            {
              backgroundColor: colorScheme === "light" ? "#f2f2f2" : "#f2f2f2",
            },
          ]}
        >
          <View
            style={[
              styles.containerContent,
              {
                width: isLargeDevice
                  ? "100%"
                  : isMediumDevice
                    ? "100%"
                    : "100%",
              },
            ]}
          >
            {isStockTransferListLoaded ? (
              <View>
                <LabelBannerWithSearchBar
                  titleName={`Total Delivery: ${deliveryPageLevelList.length}`}
                  handleSearch={onHandleSearch}
                  setRefresh={setRefresh}
                  isResetFields={isRefresh}
                  extraControls={!(isSmallDevice || isMobileDevice) ?
                    <RNView>
                      <TouchableOpacity
                        ref={settingsBtnRef}
                        style={{
                          width: isSmallDevice || isMobileDevice ? 34 : 40,
                          height: isSmallDevice || isMobileDevice ? 34 : 40,
                          borderRadius: isSmallDevice || isMobileDevice ? 8 : 10,
                          backgroundColor: columnSettingsOpen ? "#E0E7FF" : "#F3F4F6",
                          justifyContent: "center",
                          alignItems: "center",
                          borderWidth: 1,
                          borderColor: columnSettingsOpen ? "#818CF8" : "#E2E8F0",
                        }}
                        onPress={() => {
                          if (settingsBtnRef.current) {
                            settingsBtnRef.current.measureInWindow(
                              (x: number, y: number, width: number, height: number) => {
                                setSettingsBtnPos({ x, y, width, height });
                                setColumnSettingsOpen(!columnSettingsOpen);
                              }
                            );
                          } else {
                            setColumnSettingsOpen(!columnSettingsOpen);
                          }
                        }}
                      >
                        <MaterialCommunityIcons
                          name="cog-outline"
                          size={20}
                          color={columnSettingsOpen ? "#56B7FD" : "#6B7280"}
                        />
                      </TouchableOpacity>
                      <RNModal
                        visible={columnSettingsOpen}
                        transparent={true}
                        animationType="fade"
                        onRequestClose={() => setColumnSettingsOpen(false)}
                      >
                        <Pressable
                          style={{
                            flex: 1,
                            backgroundColor: "rgba(0,0,0,0.15)",
                            ...((isSmallDevice || isMobileDevice)
                              ? { justifyContent: "center", alignItems: "center", padding: 20 }
                              : {}),
                          }}
                          onPress={() => setColumnSettingsOpen(false)}
                        >
                          <Pressable
                            style={{
                              ...((isSmallDevice || isMobileDevice)
                                ? { width: "100%", maxWidth: 320 }
                                : {
                                  position: "absolute",
                                  top: settingsBtnPos.y + settingsBtnPos.height + 6,
                                  left: settingsBtnPos.x,
                                  minWidth: 220,
                                }),
                              backgroundColor: "#fff",
                              borderRadius: 12,
                              borderWidth: 1,
                              borderColor: "#E2E8F0",
                              paddingVertical: 10,
                              shadowColor: "#000",
                              shadowOffset: { width: 0, height: 8 },
                              shadowOpacity: 0.15,
                              shadowRadius: 20,
                              elevation: 10,
                            }}
                            onPress={(e) => e.stopPropagation()}
                          >
                            <Text
                              style={{
                                fontSize: 12,
                                fontWeight: "700",
                                color: "#6B7280",
                                paddingHorizontal: 16,
                                paddingBottom: 8,
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                              }}
                            >
                              Show / Hide Columns
                            </Text>
                            <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 4 }} />
                            <TouchableOpacity
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                                paddingVertical: 8,
                                paddingHorizontal: 16,
                              }}
                              onPress={() => {
                                const allKeys = Object.keys(getAllHeaders()).filter((k) => k !== "");
                                if (hiddenColumns.length === 0) {
                                  // Hide all
                                  const updated = [...allKeys];
                                  setHiddenColumns(updated);
                                  AsyncStorage.setItem("stock_transfer_hidden_columns", JSON.stringify(updated));
                                } else {
                                  // Show all
                                  setHiddenColumns([]);
                                  AsyncStorage.setItem("stock_transfer_hidden_columns", JSON.stringify([]));
                                }
                              }}
                            >
                              <View
                                style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: 4,
                                  borderWidth: 1.5,
                                  borderColor: hiddenColumns.length === 0 ? "#56B7FD" : "#D1D5DB",
                                  backgroundColor: hiddenColumns.length === 0 ? "#56B7FD" : "#fff",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  marginRight: 10,
                                }}
                              >
                                {hiddenColumns.length === 0 && (
                                  <Feather name="check" size={13} color="#fff" />
                                )}
                              </View>
                              <Text
                                style={{
                                  fontSize: 14,
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                Select All
                              </Text>
                            </TouchableOpacity>
                            <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 2 }} />
                            <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator={true}>
                              {Object.entries(getAllHeaders()).map(([key, value]) => {
                                if (key === "") return null;
                                const isVisible = !hiddenColumns.includes(key);
                                return (
                                  <TouchableOpacity
                                    key={key}
                                    style={{
                                      flexDirection: "row",
                                      alignItems: "center",
                                      paddingVertical: 8,
                                      paddingHorizontal: 16,
                                    }}
                                    onPress={() => toggleColumn(key)}
                                  >
                                    <View
                                      style={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: 4,
                                        borderWidth: 1.5,
                                        borderColor: isVisible ? "#56B7FD" : "#D1D5DB",
                                        backgroundColor: isVisible ? "#56B7FD" : "#fff",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        marginRight: 10,
                                      }}
                                    >
                                      {isVisible && (
                                        <Feather name="check" size={13} color="#fff" />
                                      )}
                                    </View>
                                    <Text
                                      style={{
                                        fontSize: 14,
                                        color: isVisible ? "#111827" : "#9CA3AF",
                                        fontWeight: isVisible ? "600" : "400",
                                      }}
                                    >
                                      {(value as any).name}
                                    </Text>
                                  </TouchableOpacity>
                                );
                              })}
                            </ScrollView>
                          </Pressable>
                        </Pressable>
                      </RNModal>
                    </RNView>
                    : undefined}
                />
                <View style={stFilterStyles.singleFilterRow}>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={stFilterStyles.filterChipRow}>
                    {/* All chip — resets everything */}
                    <TouchableOpacity
                      style={[
                        stFilterStyles.filterButton,
                        (selectedDates.length === 0 && selectedStates.length === 0 && selectedTypes.length === 0) && stFilterStyles.activeFilterButton,
                      ]}
                      onPress={() => {
                        setSelectedDates([]);
                        setSelectedStates([]);
                        setSelectedTypes([]);
                      }}
                    >
                      <Text style={(selectedDates.length === 0 && selectedStates.length === 0 && selectedTypes.length === 0) ? stFilterStyles.activeFilterText : stFilterStyles.filterText}>All</Text>
                    </TouchableOpacity>

                    {/* State chips */}
                    {["NSW", "VIC", "QLD", "WA", "SA", "TAS"].map((s, i) => {
                      const isActive = selectedStates.includes(s);
                      return (
                        <TouchableOpacity key={`state-${i}`} style={[stFilterStyles.filterButton, isActive && stFilterStyles.activeFilterButton]}
                          onPress={() => {
                            const n = isActive ? selectedStates.filter(x => x !== s) : [...selectedStates, s];
                            setSelectedStates(n);
                          }}>
                          <Text style={isActive ? stFilterStyles.activeFilterText : stFilterStyles.filterText}>{s}</Text>
                        </TouchableOpacity>
                      );
                    })}

                    {/* Date chips */}
                    {["Today", "Next 7 days", "Next 14 days", "Next 30 days"].map((d, i) => {
                      const isActive = selectedDates.includes(d);
                      return (
                        <TouchableOpacity key={`date-${i}`} style={[stFilterStyles.filterButton, isActive && stFilterStyles.activeFilterButton]}
                          onPress={() => {
                            const n = isActive ? selectedDates.filter(x => x !== d) : [...selectedDates, d];
                            setSelectedDates(n);
                          }}>
                          <Text style={isActive ? stFilterStyles.activeFilterText : stFilterStyles.filterText}>{d}</Text>
                        </TouchableOpacity>
                      );
                    })}

                    {/* Transfer Type chips */}
                    {["Transfer In", "Transfer Out"].map((t, i) => {
                      const isActive = selectedTypes.includes(t);
                      return (
                        <TouchableOpacity key={`type-${i}`} style={[stFilterStyles.filterButton, isActive && stFilterStyles.activeFilterButton]}
                          onPress={() => {
                            const n = isActive ? selectedTypes.filter(x => x !== t) : [...selectedTypes, t];
                            setSelectedTypes(n);
                          }}>
                          <Text style={isActive ? stFilterStyles.activeFilterText : stFilterStyles.filterText}>{t}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                </View>
                <DataTableWithPagination
                  headers={tableHeaders}
                  rows={replaceDataValues(deliveryPageLevelList, dataToBeConverted)}
                  itemsPerPage={20}
                  isDataLoading={isSearched}
                  useModal={true}
                  nestedHeader={nestedTableHeaders}
                  isRowsExpandable={true}
                  setModalVisible={setModalVisible}
                  handleRowClick={onHandleRowClick}
                  setSelected={setSelected}
                  isToExpandAllRows={_isDisplayViewOn}
                  rowKeyValue="DocNum"
                  nestedItemKeyValue="items"
                  isSubDetail={true}
                  isDetail={false}
                  isEdit={false}
                  isDelete={false}
                  columnsToNotHaveThousandSeparator={columnsToNotHaveThousandsSeparator}
                  handleSortColumn={onHandleSortColumn}
                  handleDeleteRow={function (id: string | number): void {
                    throw new Error("Function not implemented.");
                  }} />
              </View>
            ) : (
              <View>
                <Spinner
                  size="small"
                  color="#74BBFF"
                  styles={{ flexDirection: "row", margin: "20%" }}
                />
              </View>
            )}
          </View>
        </View>
        <Modal.ModalContainer
          modalVisible={modalVisible}
          setModalVisible={setModalVisible}
          childern={ModalContent}
        />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    flexGrow: 1,
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
  },
  fab: {
    bottom: -10,
    position: "absolute",
    zIndex: 1,
  },
});

const stFilterStyles = StyleSheet.create({
  singleFilterRow: {
    width: "100%",
    borderBottomWidth: 1,
    borderColor: "darkgrey",
    backgroundColor: "white",
    paddingVertical: 6,
    paddingHorizontal: 6,
  },
  filterRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  filterLabel: {
    fontSize: 13,
    fontWeight: "700",
    color: "#111827",
    marginRight: 10,
    minWidth: 36,
  },
  filterChipRow: {
    flexDirection: "row",
    paddingVertical: 4,
    paddingRight: 8,
  },
  filterButton: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    marginRight: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  activeFilterButton: {
    backgroundColor: "#61B8FF",
    borderColor: "#61B8FF",
  },
  filterText: {
    fontSize: 13,
    color: "#475569",
    fontWeight: "500",
  },
  activeFilterText: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600",
  },
});